import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowRight, User } from "lucide-react";
import ListingCard from "./ListingCard";

/**
 * Fix over-encoded S3 URLs
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

/**
 * MoreFromAgent - Section showing other listings by the same agent
 * Displayed on individual property gallery pages
 */
const MoreFromAgent = ({
    agent,
    listings,
    currentGalleryId,
    accentColor = "#3b82f6"
}) => {
    const [imageError, setImageError] = useState(false);
    // Filter out current listing and limit to 4
    const otherListings = (listings || [])
        .filter((listing) => listing.id !== currentGalleryId)
        .slice(0, 4);

    // Don't render if no other listings
    if (otherListings.length === 0) {
        return null;
    }

    const {
        name = "Agent",
        slug,
        title,
        company,
        photoUrl,
        listingCount = 0
    } = agent || {};

    // Decode agent photo URL (may be over-encoded S3 URL)
    const decodedPhotoUrl = decodeS3Url(photoUrl);

    return (
        <section className="py-12 bg-slate-50 border-t border-slate-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                {/* Agent Header */}
                <div className="flex items-center gap-4 mb-8">
                    {/* Agent Photo */}
                    {decodedPhotoUrl && !imageError ? (
                        <img
                            src={decodedPhotoUrl}
                            alt={name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center border-2 border-white shadow-sm">
                            <User className="w-8 h-8 text-slate-400" />
                        </div>
                    )}

                    {/* Agent Info */}
                    <div>
                        <h2 className="text-2xl font-semibold text-slate-900">
                            More from {name}
                        </h2>
                        {(title || company) && (
                            <p className="text-slate-600">
                                {title}
                                {title && company && " | "}
                                {company}
                            </p>
                        )}
                        {listingCount > 0 && (
                            <p className="text-sm text-slate-500">
                                {listingCount} Active Listing{listingCount !== 1 ? "s" : ""}
                            </p>
                        )}
                    </div>
                </div>

                {/* Listings Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {otherListings.map((listing) => (
                        <ListingCard key={listing.id} listing={listing} />
                    ))}
                </div>

                {/* View All Link */}
                {slug && listingCount > 4 && (
                    <div className="text-center mt-8">
                        <a
                            href={`/flow/agent/${slug}`}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-opacity hover:opacity-90"
                            style={{ backgroundColor: accentColor }}
                        >
                            View All {listingCount} Listings
                            <ArrowRight className="w-5 h-5" />
                        </a>
                    </div>
                )}
            </div>
        </section>
    );
};

MoreFromAgent.propTypes = {
    agent: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        company: PropTypes.string,
        photoUrl: PropTypes.string,
        title: PropTypes.string,
        brokerageLogo: PropTypes.string,
        listingCount: PropTypes.number
    }),
    listings: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            slug: PropTypes.string,
            address: PropTypes.string,
            price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            heroImageUrl: PropTypes.string,
            heroThumbnailUrl: PropTypes.string,
            photoCount: PropTypes.number
        })
    ),
    currentGalleryId: PropTypes.string,
    accentColor: PropTypes.string
};

export default MoreFromAgent;
